<template>
    <page :tabs="tabs">
        <grid
            ref="grid"
            :columnDefs="columnDefs"
            :fetchCallback="fetchCallback"
            :isAutoWidth="true"
            :isSearch="true"
            :defaultSort="sort"
            :defaultDesc="true"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            ></date-range-picker>

            <responsive-dropdown :icon="['fas', 'filter']" :items="filterByStatusOptions" variant="primary">
                {{ filterByStatus.label }}
            </responsive-dropdown>

            <responsive-button :icon="['fas', 'arrows-rotate']" variant="primary" @click="refresh"
                >Refresh</responsive-button
            >

            <responsive-button :icon="['fas', 'check']" variant="primary" @click="isMarkAsReadShown = true"
                >Mark All As Read</responsive-button
            >

            <modal
                id="mark-as-read-modal"
                title="Mark as Read"
                ok-title="Mark as Read"
                @hide="isMarkAsReadShown = false"
                @cancel="isMarkAsReadShown = false"
                @ok="markAllAsRead"
                :visible="isMarkAsReadShown"
                >Are you sure you want to mark all notifications as read?</modal
            >
        </grid>
    </page>
</template>

<script>
    import { format } from 'date-fns';
    import { mapGetters, mapMutations } from 'vuex';
    import API from '../../api';
    import { NOTIFICATION_ENDPOINT } from '../../endpoints';
    import DateRangePicker, {
        LAST_MONTH,
        LAST_WEEK,
        LAST_YEAR,
        nameToRange,
        THIS_MONTH,
        THIS_WEEK,
        THIS_YEAR,
        TODAY,
        YESTERDAY,
    } from '../generic/date-range-picker/date-range-picker.vue';
    import Grid from '../generic/grid/grid.vue';
    import Modal from '../generic/modal/modal.vue';
    import page from '../generic/page/page.vue';
    import { truncate } from '../../utils/strings';
    import FormInput from '../generic/form-input/form-input.vue';
    import SearchMultiselect from '../generic/search-multiselect/search-multiselect.vue';
    import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
    import MoreFiltersDropdown from '../generic/more-filters-dropdown/more-filters-dropdown.vue';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import StatusCellRenderer from './status-cell-renderer.vue';
    import { NOTIFICATION_DROPDOWN_MAX } from '../../constants';

    export default {
        components: {
            page,
            Grid,
            Modal,
            DateRangePicker,
            FormInput,
            SearchMultiselect,
            ExportToExcelButton,
            MoreFiltersDropdown,
            ResponsiveDropdown,
            ResponsiveButton,
            actionsCellRenderer: ActionsCellRenderer,
            statusCellRenderer: StatusCellRenderer,
        },
        name: 'NotificationPage',
        data() {
            return {
                busy: false,
                tabs: [
                    {
                        title: 'Notifications',
                        route: '/app/notification/',
                        active: this.$route.path === '/app/notification/',
                    },
                    {
                        title: 'Devices',
                        route: '/app/notification/devices/',
                        active: this.$route.path === '/app/notification/devices/',
                    },
                ],
                dateRange: nameToRange(THIS_MONTH),
                dateRequestFormat: 'yyyy-MM-dd',
                predefinedRanges: [
                    LAST_MONTH,
                    LAST_WEEK,
                    LAST_YEAR,
                    THIS_MONTH,
                    THIS_WEEK,
                    THIS_YEAR,
                    TODAY,
                    YESTERDAY,
                ],
                offset: 0,
                limit: 20,
                sort: 'date',
                desc: false,
                filterByStatus: {
                    label: 'All',
                    value: null,
                },
                isMarkAsReadShown: false,
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),

            columnDefs() {
                return [
                    {
                        field: 'is_read',
                        headerName: '',
                        cellRenderer: 'statusCellRenderer',
                        minWidth: 56,
                        maxWidth: 56,
                    },
                    {
                        field: 'date',
                        headerName: 'Date',
                        type: 'dateTimeColumn',
                    },
                    {
                        field: 'subject',
                        headerName: 'Subject',
                        minWidth: 200,
                    },
                    {
                        field: 'body',
                        sortable: false,
                        headerName: 'Message',
                        pdfExportOptions: {
                            valueFormatter: ({ value }) => {
                                return truncate(value, 50);
                            },
                        },
                        autoWidthExclude: true,
                    },
                    {
                        field: '',
                        headerName: 'Actions',
                        sortable: false,
                        cellRenderer: 'actionsCellRenderer',
                    },
                ];
            },
            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    let is_show_all = false;
                    let is_read = this.filterByStatus.value;
                    if (this.filterByStatus.value === null) {
                        is_show_all = true;
                        is_read = false;
                    }
                    return API.get(`${NOTIFICATION_ENDPOINT()}notifications/`, {
                        params: {
                            offset,
                            limit,
                            sort: sort,
                            desc,
                            is_read: is_read,
                            is_show_all: is_show_all,
                            start_date: this.startDateRequest,
                            end_date: this.endDateRequest,
                            search: search,
                        },
                    });
                };
            },
            filterByStatusOptions() {
                const all = {
                    label: 'All',
                    value: null,
                };
                const unread = {
                    label: 'Unread',
                    value: false,
                };
                const read = {
                    label: 'Read',
                    value: true,
                };
                return [
                    {
                        label: all.label,
                        active: this.filterByStatus.value === all.value,
                        click: () => {
                            this.filterByStatus = all;
                            this.refresh();
                        },
                        dropDownItemClass: 'filter-by-status-dropdown-item',
                    },
                    {
                        label: unread.label,
                        active: this.filterByStatus.value === unread.value,
                        click: () => {
                            this.filterByStatus = unread;
                            this.refresh();
                        },
                        dropDownItemClass: 'filter-by-status-dropdown-item',
                    },
                    {
                        label: read.label,
                        active: this.filterByStatus.value === read.value,
                        click: () => {
                            this.filterByStatus = read;
                            this.refresh();
                        },
                        dropDownItemClass: 'filter-by-status-dropdown-item',
                    },
                ];
            },
        },
        mounted() {},
        methods: {
            ...mapMutations('header', ['setNotificationList']),
            handleDaterangeChange(value) {
                this.dateRange = { ...value };
                this.refresh();
            },
            refresh() {
                this.$refs.grid.refreshDataSource();
            },
            async markAllAsRead() {
                await API.patch(`${NOTIFICATION_ENDPOINT()}notifications_set_read_all/`);
                this.refresh();
                const notificationList = await API.get(`${NOTIFICATION_ENDPOINT()}notifications/`, {
                    params: {
                        offset: 0,
                        limit: NOTIFICATION_DROPDOWN_MAX,
                        sort: 'date',
                        desc: true,
                        is_read: false,
                    },
                });
                this.setNotificationList(notificationList.data);
            },
        },
    };
</script>

<style lang="scss" scoped>
    .subscribers-list-wrapper {
        top: 40px;
    }
    .subscribers-wrapper {
        min-width: 300px;
    }
</style>
